import { useCallback, useEffect, useState } from 'react';
import { api, estSessionExpiree } from './api';
import type { Moi } from '../partage/types';
import { Connexion } from './pages/Connexion';
import { MotDePasseOublie, NouveauMotDePasse } from './pages/MotDePasse';
import { MonCompte } from './pages/MonCompte';
import { Texte } from './pages/Textes';
import { Arbre } from './pages/Arbre';
import { Rapprochements } from './pages/Rapprochements';
import { Demandes } from './pages/Demandes';
import {
  Chargement,
  IconeArbre,
  IconeEnveloppe,
  IconeLiens,
  IconeMarque,
  IconeQuitter,
  nomComplet,
  useEnLigne,
  useFileAttente,
} from './composants/base';
import { Avatar } from './composants/Avatar';
import { brancherSynchro } from './synchro';

type Onglet = 'arbre' | 'rapprochements' | 'demandes' | 'compte';

/**
 * Routage minimal, lu dans l'adresse. Deux écrans doivent être
 * atteignables par un lien reçu par courriel, donc sans être connecté :
 * la réinitialisation du mot de passe et la confirmation d'adresse.
 * Une bibliothèque de routage complète serait disproportionnée pour deux
 * chemins et coûterait une dizaine de kilo-octets.
 */
function cheminInitial(): { page: string; jeton: string } {
  if (typeof window === 'undefined') return { page: '/', jeton: '' };
  const url = new URL(window.location.href);
  return { page: url.pathname, jeton: url.searchParams.get('jeton') ?? '' };
}

function effacerAdresse() {
  if (typeof window !== 'undefined') {
    window.history.replaceState({}, '', '/');
  }
}

const TITRES: Record<Onglet, string> = {
  arbre: 'Mon arbre',
  rapprochements: 'Rapprochements',
  demandes: 'Demandes',
  compte: 'Mon compte',
};

export default function App() {
  const [moi, setMoi] = useState<Moi | null | undefined>(undefined);
  const [onglet, setOnglet] = useState<Onglet>('arbre');
  const [aVerifier, setAVerifier] = useState({ rapprochements: 0, demandes: 0 });
  // L'arbre dessiné réclame toute la page : pas de marges, pas de pied.
  const [pleinePage, setPleinePage] = useState(false);
  const [route, setRoute] = useState(cheminInitial);
  const [texte, setTexte] = useState<'confidentialite' | 'conditions' | null>(null);
  const enLigne = useEnLigne();
  const file = useFileAttente();
  const [bilan, setBilan] = useState<string | null>(null);

  const enAttente = file.filter((e) => !e.erreur).length;
  const refusees = file.filter((e) => e.erreur).length;

  const chargerMoi = useCallback(async () => {
    try {
      setMoi(await api.moi());
    } catch (e) {
      if (estSessionExpiree(e)) setMoi(null);
      else setMoi(null);
    }
  }, []);

  const chargerPastilles = useCallback(async () => {
    try {
      const [r, d] = await Promise.all([api.rapprochements(), api.demandes()]);
      setAVerifier({
        rapprochements: r.filter(
          (x) => x.statut !== 'confirme' && !x.j_ai_confirme,
        ).length,
        demandes: d.length,
      });
    } catch {
      /* hors ligne : on garde les derniers compteurs connus */
    }
  }, []);

  useEffect(() => {
    void chargerMoi();
  }, [chargerMoi]);

  useEffect(() => {
    if (moi) void chargerPastilles();
  }, [moi, chargerPastilles]);

  // La file part dès que le réseau revient, et une fois à l'ouverture :
  // le téléphone a pu être rallumé déjà connecté, sans jamais émettre
  // l'évènement « online ».
  const connecte = moi != null;

  useEffect(() => {
    if (!connecte) return;
    return brancherSynchro((b) => {
      setBilan(
        b.refusees > 0
          ? `${b.envoyees} déclaration(s) envoyée(s), ${b.refusees} refusée(s).`
          : `${b.envoyees} déclaration(s) envoyée(s).`,
      );
      void chargerMoi();
      void chargerPastilles();
    });
  }, [connecte, chargerMoi, chargerPastilles]);

  const reprendre = () => {
    effacerAdresse();
    setRoute({ page: '/', jeton: '' });
    setMoi(undefined);
    void chargerMoi();
  };

  // Les écrans atteints par un lien reçu par courriel passent avant tout
  // le reste : ils doivent fonctionner sans session.
  if (route.page === '/nouveau-mot-de-passe' && route.jeton) {
    return (
      <div className="contenu" style={{ paddingTop: 28 }}>
        <NouveauMotDePasse jeton={route.jeton} onFait={reprendre} />
      </div>
    );
  }


  if (moi === undefined) return <Chargement texte="Ouverture de Yambi…" />;

  if (moi === null) {
    if (route.page === '/mot-de-passe-oublie') {
      return (
        <div className="contenu" style={{ paddingTop: 28 }}>
          <MotDePasseOublie
            onRetour={() => setRoute({ page: '/', jeton: '' })}
          />
        </div>
      );
    }
    return (
      <Connexion
        onConnecte={() => {
          setMoi(undefined);
          void chargerMoi();
        }}
        onOublie={() => setRoute({ page: '/mot-de-passe-oublie', jeton: '' })}
      />
    );
  }

  async function deconnecter() {
    try {
      await api.deconnexion();
    } finally {
      setMoi(null);
    }
  }

  const onglets: { cle: Onglet; icone: JSX.Element; pastille: number }[] = [
    { cle: 'arbre', icone: <IconeArbre />, pastille: 0 },
    { cle: 'rapprochements', icone: <IconeLiens />, pastille: aVerifier.rapprochements },
    { cle: 'demandes', icone: <IconeEnveloppe />, pastille: aVerifier.demandes },
  ];

  return (
    <div className="app">
      <header className="entete">
        <IconeMarque className="marque" />
        <h1>{TITRES[onglet]}</h1>
        <div className="espace" />
        <button
          className="entete-moi"
          onClick={() => {
            setPleinePage(false);
            setTexte(null);
            setOnglet('compte');
          }}
          aria-label="Mon compte"
        >
          <Avatar
            id={moi.personne_id}
            sexe={moi.sexe}
            taille={26}
            inconnu={!moi.nom && !moi.prenoms}
          />
          <span className="entete-nom">{nomComplet(moi.nom, moi.prenoms)}</span>
        </button>
        <button
          className="bouton discret quitter"
          onClick={deconnecter}
          aria-label="Quitter"
        >
          <IconeQuitter className="quitter-icone" />
          <span className="quitter-texte">Quitter</span>
        </button>
      </header>

      {!enLigne && (
        <div className="hors-ligne" role="status">
          {enAttente > 0
            ? `Hors ligne — ${enAttente} déclaration${
                enAttente > 1 ? 's' : ''
              } en attente, elles partiront dès le retour du réseau.`
            : 'Hors ligne — vous consultez la dernière version enregistrée, et vous pouvez continuer à déclarer.'}
        </div>
      )}

      {enLigne && enAttente > 0 && (
        <div className="hors-ligne" role="status">
          Envoi de {enAttente} déclaration{enAttente > 1 ? 's' : ''} en cours…
        </div>
      )}

      {enLigne && refusees > 0 && (
        <button
          className="bandeau-verification"
          onClick={() => {
            setPleinePage(false);
            setBilan(null);
            setOnglet('arbre');
          }}
        >
          {refusees} déclaration{refusees > 1 ? 's' : ''} refusée
          {refusees > 1 ? 's' : ''} par le serveur →
        </button>
      )}

      {bilan && (
        <button
          className="bandeau-verification"
          onClick={() => setBilan(null)}
        >
          {bilan}
        </button>
      )}

      {enLigne && !moi.email_verifie && moi.verification_disponible &&
        onglet !== 'compte' && (
        <button
          className="bandeau-verification"
          onClick={() => {
            setPleinePage(false);
            setOnglet('compte');
          }}
        >
          Confirmez votre adresse pour pouvoir récupérer votre compte →
        </button>
      )}

      <main className={`contenu${pleinePage ? ' contenu-plein' : ''}`}>
        {onglet === 'arbre' && (
          <Arbre
            moi={moi}
            onRafraichir={chargerPastilles}
            onPleinePage={setPleinePage}
          />
        )}
        {onglet === 'rapprochements' && (
          <Rapprochements onChangement={chargerPastilles} />
        )}
        {onglet === 'demandes' && <Demandes onChangement={chargerPastilles} />}
        {onglet === 'compte' &&
          (texte ? (
            <Texte quoi={texte} onRetour={() => setTexte(null)} />
          ) : (
            <MonCompte
              moi={moi}
              onDeconnecte={() => setMoi(null)}
              onRafraichir={chargerMoi}
              onVoirTexte={setTexte}
            />
          ))}

      </main>

      <nav className="navigation" aria-label="Navigation principale">
        {onglets.map(({ cle, icone, pastille }) => (
          <button
            key={cle}
            onClick={() => {
              if (cle !== 'arbre') setPleinePage(false);
              setOnglet(cle);
            }}
            aria-current={onglet === cle ? 'page' : undefined}
          >
            {icone}
            {TITRES[cle]}
            {pastille > 0 && (
              <span className="pastille" aria-label={`${pastille} à traiter`}>
                {pastille}
              </span>
            )}
          </button>
        ))}
      </nav>
    </div>
  );
}
